<template>
    <div class="card-upload">
        <div class="body">
            <el-form ref="formRef" :model="formData" label-position="top" class="form">
                <el-form-item class="bg-item" required label-width="0" style="padding-left: 10px;padding-right: 10px;">
                    <div class="bg-header">
                        <div class="bg-label">
                            <span class="req">*</span>
                            <span>背景图</span>
                            <span class="bg-hint">（1620 x 1080 px）</span>
                        </div>
                        <div class="bg-lang-tabs">
                            <span v-for="l in languages" :key="l" class="bg-lang-tab"
                                :class="{ active: currentLang === l }" @click="currentLang = l">
                                {{ l === "zh_CN" ? "简体" : (l === "zh_TW" ? "繁体" : "英文") }}
                            </span>
                        </div>
                    </div>

                    <div class="bg-uploader">
                        <el-upload :class="currentThumbUrl ? 'bg-upload-preview' : 'bg-upload-full'" action=""
                            :auto-upload="false" :show-file-list="false" :on-change="handleFileChange"
                            accept=".png,.jpg,.jpeg,.bmp,.svg">
                            <div v-if="!currentThumbUrl" class="bg-upload-inner">
                                <el-button type="primary">添加背景图</el-button>
                            </div>
                            <div v-else class="bg-preview">
                                <el-image :src="currentThumbUrl" fit="cover" class="bg-preview-img" />
                                <i class="el-icon-close bg-remove" @click.stop="removeFile"></i>
                            </div>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-row :gutter="0" class="two-col-row">
                    <el-col :span="12">
                        <el-form-item label="标题" required>
                            <el-input v-model="currentLangValue('title').value" placeholder="请输入" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="宣传词">
                            <el-input v-model="currentLangValue('slogan').value" placeholder="请输入" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="0" class="two-col-row">
                    <el-col :span="12">
                        <el-form-item label="缩略图角标">
                            <el-input v-model="currentLangValue('thumbnailBadge').value" placeholder="请输入" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12"></el-col>
                </el-row>

                <el-form-item label="标签" style="padding-left: 10px;padding-right: 10px;">
                    <el-select v-model="formData.tagIds" multiple filterable clearable placeholder="请选择标签"
                        style="width: 100%;" :popper-append-to-body="false" popper-class="tag-select-popper">
                        <el-option v-for="t in tagOptions" :key="t.value" :label="t.label" :value="t.value" />
                    </el-select>
                </el-form-item>

                <el-form-item label="发布人" required style="padding-left: 10px;padding-right: 10px;">
                    <el-select v-model="formData.publisherId" filterable clearable placeholder="请选择发布人"
                        style="width: 100%;">
                        <el-option v-for="p in publisherOptions" :key="p.value" :label="p.label" :value="p.value" />
                    </el-select>
                </el-form-item>
                <el-form-item label="推送配置" style="padding-left: 10px;padding-right: 10px;">
                    <push-config ref="pushConfigRef" />
                </el-form-item>

                <el-form-item label="跳转配置" style="padding-left: 10px;padding-right: 10px;">
                    <nav-config ref="navConfigRef" type="RESOURCE" />
                </el-form-item>
            </el-form>
        </div>

        <div class="footer">
            <el-button @click="$emit('close')">取消</el-button>
            <el-button type="primary" :loading="loading" @click="handleSubmit">确认</el-button>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import { getDefaultLanguageData, valueByLanguage, checkLangCompleteness } from "@/utils/college";
import { getMd5, uploadFileToOssByPath, checkLanguageData } from "@/utils/uploadUtils";
import PushConfig from "@/components/PushConfig/index.vue";
import NavConfig from "@/components/NavConfig.vue";
import { mapGetters } from "vuex";

// 对齐galaxy：定义OSSApiKey常量
const OSSApiKey = {
    Common: "common",
    Message: "message"
};

export default {
    name: "CardMaterialUploadData",
    components: {
        PushConfig,
        NavConfig
    },
    props: {
        moduleId: {
            type: Number,
            default: null
        },
        detailId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            languages: ["zh_CN", "zh_TW", "en"],
            currentLang: "zh_CN",
            tagOptions: [],
            publisherOptions: [],
            tempFileByLang: {
                zh_CN: null,
                zh_TW: null,
                en: null
            },
            revokeUrls: new Map(),
            formData: {
                moduleId: this.moduleId,
                resourceType: "RESOURCE",
                title: getDefaultLanguageData(),
                slogan: getDefaultLanguageData(),
                thumbnailBadge: getDefaultLanguageData(),
                // 这里简化：直接存 url 到 value（后端若收 assetId，后续再按 galaxy 补齐文件服务）
                bgFileAssetId: getDefaultLanguageData(),
                tagIds: [],
                publisherId: null,
                pushConditions: [],
                navigationConfig: { navType: "NoNavigation" }
            }
        };
    },
    computed: {
        ...mapGetters(["bucketData"]), // 对齐galaxy：使用bucketData getter
        currentThumbUrl() {
            // 仅根据临时文件里的 url 显示预览
            // 编辑场景下，initTempFile 会根据 bgFileAssetId 填充 tempFileByLang
            const f = this.tempFileByLang[this.currentLang];
            return (f && f.url) ? f.url : "";
        }
    },
    watch: {
        moduleId: {
            immediate: true,
            handler(v) {
                this.formData.moduleId = v;
            }
        },
        detailId: {
            immediate: true,
            async handler(newVal, oldVal) {
                // 当 detailId 从 null 变为有值时，加载数据
                if (newVal && newVal !== oldVal) {
                    // 确保 tagOptions 和 publisherOptions 已加载
                    if (this.tagOptions.length === 0 || this.publisherOptions.length === 0) {
                        await Promise.all([this.getTagOptions(), this.getPublisherOptions()]);
                    }
                    await this.getDetail();
                } else if (!newVal && oldVal) {
                    // console.log('resetFormData');
                    this.resetFormData();
                }
            }
        }
    },
    mounted() {
        this.bootstrap();
    },
    beforeDestroy() {
        this.revokeAll();
    },
    methods: {
        async bootstrap() {
            await Promise.all([this.getTagOptions(), this.getPublisherOptions()]);
            // 如果 detailId 存在，加载详情数据
            // 注意：由于使用了 watch，detailId 变化时会自动触发 getDetail
            // 但 mounted 时如果 detailId 已存在，也需要加载
            if (this.detailId) {
                await this.getDetail();
            }
        },
        resetFormData() {
            // 重置表单数据
            this.formData = {
                moduleId: this.moduleId,
                resourceType: "RESOURCE",
                title: getDefaultLanguageData(),
                slogan: getDefaultLanguageData(),
                thumbnailBadge: getDefaultLanguageData(),
                bgFileAssetId: getDefaultLanguageData(),
                tagIds: [],
                publisherId: null,
                pushConditions: [],
                navigationConfig: { navType: "NoNavigation" }
            };
            // 清空临时文件
            this.tempFileByLang = {
                zh_CN: null,
                zh_TW: null,
                en: null
            };
            // 清空 blob URL
            this.revokeAll();
            // 重置推送和跳转配置
            this.$nextTick(() => {
                if (this.$refs.pushConfigRef && this.$refs.pushConfigRef.initData) {
                    this.$refs.pushConfigRef.initData([]);
                }
                if (this.$refs.navConfigRef && this.$refs.navConfigRef.initData) {
                    this.$refs.navConfigRef.initData({ navType: "NoNavigation" });
                }
            });
        },
        currentLangValue(field) {
            const arr = this.formData[field];
            if (!Array.isArray(arr)) return { locale: this.currentLang, value: "" };
            let found = arr.find((x) => x.locale === this.currentLang);
            if (!found) {
                found = { locale: this.currentLang, value: "" };
                arr.push(found);
            }
            return found;
        },
        async getTagOptions() {
            try {
                const res = await api.getMaterialTagList({ page: 1, pageSize: 1000 });
                const items = (res.data && res.data.items) ? res.data.items : [];
                this.tagOptions = items.map((it) => ({
                    value: it.id,
                    label: valueByLanguage(it.name, "zh_CN", true) || it.id
                }));
            } catch (e) {
                this.tagOptions = [];
            }
        },
        async getPublisherOptions() {
            try {
                const res = await api.getMaterialPublisherList({ page: 1, pageSize: 1000 });
                const items = (res.data && res.data.items) ? res.data.items : [];
                this.publisherOptions = items.map((it) => ({
                    value: it.id,
                    label: valueByLanguage(it.name, "zh_CN", true) || it.id
                }));
            } catch (e) {
                this.publisherOptions = [];
            }
        },
        handleFileChange(file) {
            const raw = file && (file.raw || file);
            if (!raw) return;
            const format = raw.name.split(".").pop();
            const acceptFormats = [".png", ".jpg", ".jpeg", ".bmp", ".svg"];
            if (!acceptFormats.includes(`.${format.toLowerCase()}`)) {
                this.$message.error(`只支持${acceptFormats.join(", ")}`);
                return;
            }
            const url = URL.createObjectURL(raw);
            this.revokeUrls.set(this.currentLang, url);
            // 对齐galaxy：从store中动态获取bucket（不写死）
            const commonBucketData = this.bucketData && this.bucketData[OSSApiKey.Common];
            const bucket = (commonBucketData && commonBucketData.bucket) || "onyx-edu"; // 默认值
            // 对齐 galaxy：保存 file、url 和 data
            this.tempFileByLang[this.currentLang] = {
                file: raw,
                url,
                data: {
                    name: raw.name,
                    folder: "",
                    size: raw.size,
                    bucket: bucket,
                    md5: ""
                }
            };
        },
        removeFile() {
            const f = this.tempFileByLang[this.currentLang];
            if (f && f.url) {
                URL.revokeObjectURL(f.url);
                this.revokeUrls.delete(this.currentLang);
            }
            this.tempFileByLang[this.currentLang] = null;
            const item = this.currentLangValue("bgFileAssetId");
            if (item) item.value = "";
        },
        revokeAll() {
            try {
                this.revokeUrls.forEach((url) => {
                    URL.revokeObjectURL(url);
                });
                this.revokeUrls.clear();
            } catch (e) { }
        },
        async uploadFiles() {
            // 对齐 galaxy：只上传有 file 的项
            const filesToUpload = await Promise.all(
                Object.entries(this.tempFileByLang)
                    .filter(([_, item]) => item && item.file)
                    .map(async ([lang, item]) => {
                        if (item && item.file) {
                            const { data, file } = item;
                            const md5 = await getMd5(file);
                            data.md5 = md5;
                            data.folder = "res_card/resource/";
                            return {
                                lang,
                                ...item
                            };
                        }
                    })
            );
            if (!filesToUpload.length) return;

            // 对齐galaxy：从store中动态获取bucket（不写死）
            const commonBucketData = this.bucketData && this.bucketData[OSSApiKey.Common];
            const bucket = (commonBucketData && commonBucketData.bucket) || "onyx-edu"; // 默认值

            const res = await api.batchCreateFiles({
                items: filesToUpload.map(f => f.data),
                headers: { "client-type": "boox" }
            });
            const fileAssetInfos = (res.data && res.data.items) ? res.data.items : [];
            if (!fileAssetInfos.length) {
                this.$message.error("文件处理失败");
                throw new Error("文件处理失败");
            }

            // 对齐 galaxy：按顺序上传并更新 fileAssetId
            await Promise.all(
                filesToUpload.map(async ({ lang, file, data }, index) => {
                    const currentFileAsset = fileAssetInfos[index];
                    if (!currentFileAsset) return;

                    // 对齐galaxy：使用后端返回的bucket，如果没有则从store获取
                    const uploadBucket = currentFileAsset.bucket || bucket;
                    await uploadFileToOssByPath({
                        file,
                        path: currentFileAsset.path,
                        bucket: uploadBucket
                    });

                    // 对齐 galaxy：更新 bgFileAssetId
                    const item = this.formData.bgFileAssetId.find(t => t.locale === lang);
                    if (item) {
                        item.value = currentFileAsset.id.toString();
                    } else {
                        // 如果不存在，创建新的语言项
                        this.formData.bgFileAssetId.push({
                            locale: lang,
                            value: currentFileAsset.id.toString()
                        });
                    }

                    // 更新预览 url（对齐 galaxy）
                    if (this.tempFileByLang[lang]) {
                        this.tempFileByLang[lang].url = currentFileAsset.url;
                        this.tempFileByLang[lang].file = null;
                    }
                })
            );
        },
        async validate() {
            // 对齐 galaxy 校验：标题必填（至少简体），发布人必填，背景图多语言完整性校验
            const titleZh = (this.formData.title.find((x) => x.locale === "zh_CN") || {}).value;
            if (!titleZh) {
                this.$message.error("标题不能为空（简体）");
                return false;
            }

            if (!this.formData.publisherId) {
                this.$message.error("发布人不能为空");
                return false;
            }

            // 背景图：三语言都必须有（对齐 galaxy checkLangCompleteness）
            // 检查 tempFileByLang，每个语言必须有 file 或 url
            const checkResult = checkLangCompleteness(this.tempFileByLang, "背景图");
            if (!checkResult.isValid) {
                // 再次检查：如果 tempFile 没有，检查是否已有 fileAssetId
                const allValid = this.languages.every(lang => {
                    const hasTemp = this.tempFileByLang[lang] && (this.tempFileByLang[lang].file || this.tempFileByLang[lang].url);
                    const hasId = (this.formData.bgFileAssetId || []).some((x) => x.locale === lang && x.value);
                    return hasTemp || hasId;
                });
                if (!allValid) {
                    this.$message.error(checkResult.message);
                    return false;
                }
            }
            return true;
        },
        async handleSubmit() {
            try {
                this.loading = true;
                // 对齐 galaxy：先校验
                if (!(await this.validate())) return;
                // 对齐 galaxy：上传文件
                await this.uploadFiles();
                // 对齐 galaxy：获取推送和跳转配置
                if (this.$refs.pushConfigRef && this.$refs.pushConfigRef.getData) {
                    this.formData.pushConditions = this.$refs.pushConfigRef.getData();
                }
                if (this.$refs.navConfigRef && this.$refs.navConfigRef.getData) {
                    this.formData.navigationConfig = this.$refs.navConfigRef.getData();
                }
                // 对齐 galaxy：提交前删除空语言项
                checkLanguageData(this.formData, [{ field: "thumbnailBadge" }, { field: "slogan" }], "del");

                if (this.detailId) {
                    await api.updateCardMaterial(this.detailId, this.formData);
                } else {
                    await api.addCardMaterial(this.formData);
                }
                this.$message.success("操作成功");
                this.$emit("success");
            } catch (e) {
                console.log(e);
                this.$message.error((e && e.message) || "保存失败");
                // 对齐 galaxy：失败时补回缺失语言项
                checkLanguageData(this.formData, [{ field: "thumbnailBadge" }, { field: "slogan" }], "add");
            } finally {
                this.loading = false;
            }
        },
        async getDetail() {
            if (!this.detailId) return;
            try {
                this.loading = true;
                const res = await api.getCardMaterialDetails(this.detailId);
                const data = res.data || res || {};
                // 对齐 galaxy：只覆盖 formData 中存在的字段
                Object.keys(data).forEach((key) => {
                    if (this.formData.hasOwnProperty(key)) {
                        this.formData[key] = data[key];
                    }
                });
                // 对齐 galaxy：补充缺失的语言数据
                checkLanguageData(this.formData, [{ field: "thumbnailBadge" }, { field: "slogan" }], "add");
                // 对齐 galaxy：tags -> tagIds
                if (data.tags && Array.isArray(data.tags)) {
                    this.formData.tagIds = data.tags.map((item) => item.id);
                }
                // 对齐 galaxy：初始化临时文件（从 fileAssetId 获取文件信息）
                await this.initTempFile();
                // 对齐 galaxy：回填推送和跳转配置
                if (this.formData.pushConditions && this.formData.pushConditions.length) {
                    this.$nextTick(() => {
                        if (this.$refs.pushConfigRef && this.$refs.pushConfigRef.initData) {
                            this.$refs.pushConfigRef.initData(this.formData.pushConditions);
                        }
                    });
                }
                if (this.formData.navigationConfig && this.formData.navigationConfig.navType) {
                    this.$nextTick(() => {
                        if (this.$refs.navConfigRef && this.$refs.navConfigRef.initData) {
                            this.$refs.navConfigRef.initData(this.formData.navigationConfig);
                        }
                    });
                }
            } catch (error) {
                console.error("getDetail error:", error);
                this.$message.error("获取详情失败：" + (error.message || error));
            } finally {
                this.loading = false;
            }
        },
        async initTempFile() {
            // 对齐 galaxy：从 bgFileAssetId 获取文件 ID，然后查询文件详情
            const ids = (this.formData.bgFileAssetId || [])
                .map((item) => Number(item.value))
                .filter((n) => !Number.isNaN(n) && n > 0);
            if (!ids.length) return;

            const res = await api.getFileConfigById(ids);
            const fileAssetInfos = (res.data && res.data.items) ? res.data.items : [];
            // 对齐 galaxy：填充 tempFileByLang，保存 url 和 data（不包含 file 字段）
            fileAssetInfos.forEach((fileInfo) => {
                const data = this.formData.bgFileAssetId.find(
                    (item) => Number(item.value) === fileInfo.id
                );
                if (data) {
                    this.tempFileByLang[data.locale] = {
                        url: fileInfo.url,
                        data: {
                            folder: fileInfo.path ? fileInfo.path.split("/")[0] : "",
                            name: fileInfo.name,
                            size: fileInfo.size,
                            md5: fileInfo.md5,
                            bucket: fileInfo.bucket
                        }
                    };
                }
            });
        }
    }
};
</script>

<style scoped>
.card-upload {
    height: calc(100vh - 120px);
    display: flex;
    flex-direction: column;
}

.body {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px;
}

.form {
    max-width: 980px;
}

:deep(.el-form-item__label) {
    white-space: nowrap;
}

.two-col-row {
    display: flex;
}

.two-col-row :deep(.el-col) {
    padding-left: 10px !important;
    padding-right: 10px !important;
}

:deep(.el-form-item.is-required .el-form-item__label) {
    display: inline-flex;
    align-items: center;
}

:deep(.el-form-item.is-required .el-form-item__label:before) {
    margin-right: 4px;
}

:deep(.el-form--label-top .el-form-item__label) {
    line-height: 18px;
    padding: 0 0 6px 0;
    white-space: nowrap;
}

:deep(.el-form--label-top .el-form-item) {
    margin-bottom: 18px;
}

.bg-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 10px;
}

.bg-lang-tabs {
    display: flex;
    gap: 12px;
}

.bg-lang-tab {
    cursor: pointer;
    font-size: 13px;
    color: #606266;
}

.bg-lang-tab.active {
    color: #409eff;
    font-weight: 600;
}

.bg-label {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.req {
    color: #f56c6c;
}

.bg-hint {
    color: #f56c6c;
    font-size: 12px;
}

.bg-uploader {
    width: 100%;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.bg-upload-full {
    width: 100%;
    height: 100%;
}

.bg-upload-full :deep(.el-upload) {
    width: 100%;
    height: 100%;
}

.bg-upload-inner {
    width: 100%;
    height: 100%;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.3s;
}

.bg-upload-inner:hover {
    border-color: #409eff;
}

.bg-upload-preview {
    width: 100px;
    height: 100px;
}

.bg-upload-preview :deep(.el-upload) {
    width: 100%;
    height: 100%;
}

.bg-preview {
    position: relative;
    width: 100px;
    height: 100px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
}

.bg-preview-img {
    width: 100%;
    height: 100px;
    object-fit: cover;
}

.bg-remove {
    position: absolute;
    right: 8px;
    top: 8px;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    line-height: 18px;
    text-align: center;
    font-size: 12px;
}

.placeholder-box {
    width: 100%;
    height: 36px;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    color: #909399;
    background: #fafafa;
}

.footer {
    border-top: 1px solid #e5e7eb;
    padding: 12px 16px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

:deep(.tag-select-popper.el-select-dropdown) {
    max-width: 100%;
}

:deep(.tag-select-popper .el-select-dropdown__wrap) {
    max-height: 320px;
}

:deep(.tag-select-popper .el-select-dropdown__item) {
    white-space: normal;
    height: auto;
    line-height: 20px;
    padding-top: 6px;
    padding-bottom: 6px;
}
</style>
